// Global Instructions Rule Applied!
//
// useEmailCampaignAccountFiles — a campaign's per-account attachments for the
// compose form (capital call notices, stage 5): the files that belong to one
// account each, and the summary the queue gate checks.
//
// Mirrors useEmailCampaignCopies: module-local state, no Redux pre-fetch;
// cold-loads on mount from the campaignId in the form URL (CLAUDE.md
// hard-refresh rule). Read-only - nothing on the form writes these files.
// `refetch` is for the form to call after the staged recipients change,
// since which accounts lack a file depends on who is staged.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  carriesAccountFiles,
  type EmailCampaignAccountFiles,
  type EmailCampaignAccountFilesRow
} from '../../../state_manager/data_models/email_campaign_account_files_model.ts'
import { fetchEmailCampaignAccountFiles } from '../services/emailService.ts'

export interface UseEmailCampaignAccountFilesOptions {
  /** Null = the compose form is in 'new' mode (no draft persisted yet); the
   *  hook stays inert. */
  campaignId: string | null
}

export const useEmailCampaignAccountFiles = ({ campaignId }: UseEmailCampaignAccountFilesOptions) => {
  const [accountFiles, setAccountFiles] = useState<EmailCampaignAccountFiles | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const cancelledRef = useRef(false)
  useEffect(() => {
    cancelledRef.current = false
    return () => {
      cancelledRef.current = true
    }
  }, [])

  const refetch = useCallback(async () => {
    if (!campaignId) {
      setAccountFiles(null)
      setError(null)
      return
    }
    setLoading(true)
    const token = getAccessToken(store.getState().auth)
    const res = await fetchEmailCampaignAccountFiles(campaignId, token)
    if (cancelledRef.current) return
    setAccountFiles(res.data)
    setError(res.error ?? null)
    setLoading(false)
  }, [campaignId])

  useEffect(() => {
    refetch()
  }, [refetch])

  const summary = accountFiles?.prog__summary ?? null

  /** accounts__id → that account's row (its files). */
  const byAccountId = useMemo(() => {
    const map = new Map<string, EmailCampaignAccountFilesRow>()
    for (const row of accountFiles?.data ?? []) map.set(row.accounts__id, row)
    return map
  }, [accountFiles])

  return {
    accountFiles,
    summary,
    byAccountId,
    /** The campaign carries per-account attachments (required, or any file). */
    carriesFiles: carriesAccountFiles(summary),
    loading,
    error,
    refetch
  }
}

export type EmailCampaignAccountFilesState = ReturnType<typeof useEmailCampaignAccountFiles>
